import { z } from 'zod';
import { isoDateSchema } from './common.js';
import { timelineEventTypeSchema } from './timeline.js';
import { feriadoSchema } from './external-data.js';

/**
 * Wave 379BN+ · #87 slice (b) — endpoint unificado `/v1/projects/:id/timeview`.
 *
 * O canvas único do TimeCanvas precisa ler 3 fontes que hoje vivem em
 * endpoints separados (timeline, calendar, gantt) + feriados como overlay.
 * Esta é a representação CANÔNICA — line/grid view consomem `events`,
 * gantt consome `stages` + `dependencies`, todos os modos pintam
 * `holidays` em cima. RUP fica fora desta resposta (continua em
 * `/v1/rup/:projectId` como dimensão dedicada de produtividade).
 *
 * Endpoints antigos (`/v1/gantt/:id`, `/v1/calendar`, `/v1/timeline`,
 * `/v1/rup`) seguem vivos durante o port — esta rota é aditiva.
 */

/** Origem do evento na resposta unificada. */
export const timeviewEventSourceSchema = z.enum([
  'timeline_event',     // veio de timeline_events (histórico real)
  'calendar_derived',   // derivado de outras tabelas (issues, POs, medições, stages planejadas)
]);
export type TimeviewEventSource = z.infer<typeof timeviewEventSourceSchema>;

/**
 * Tipo unificado de evento. Une `TimelineEventType` (eventos históricos
 * em `timeline_events`) com os 7 `kind`s do calendar agregado. Mantemos
 * a string literal pra cada origem em vez de fundir — isso preserva
 * informação no client (ex.: `stage_started` é REAL, `stage_start` é
 * PLANEJADO; o canvas precisa saber a diferença pra dimensão
 * planejado-vs-realizado).
 */
export const timeviewEventKindSchema = z.union([
  timelineEventTypeSchema,
  z.enum([
    'rdo',
    'issue',
    'medicao',
    'po_delivery',
    'po_issued',
    'stage_start',
    'stage_end',
  ]),
]);
export type TimeviewEventKind = z.infer<typeof timeviewEventKindSchema>;

/**
 * Evento canônico no canvas. `at` é ISO datetime quando vier de
 * timeline_events, ou ISO date (YYYY-MM-DD) quando for derivado do
 * calendar (que opera em granularidade dia). O client trata com
 * `at.length === 10 ? date-only : datetime`.
 *
 * Wave 379CK · C11 fatia 4a · #125 — campos `description`/`createdBy`/
 * `createdByName`/`createdAt`/`photoCount` adicionados como opcionais
 * pra que TimelinePage possa hidratar do timeview e dispensar o fetch
 * separado em `/v1/projects/:id/timeline`. Rows com source='timeline_event'
 * populam todos esses campos; rows 'calendar_derived' deixam undefined
 * (não há entidade autora ou foto pra um RDO/PO/medição derivado).
 */
export const timeviewEventSchema = z.object({
  id: z.string(),
  source: timeviewEventSourceSchema,
  kind: timeviewEventKindSchema,
  at: z.string(),
  title: z.string(),
  status: z.string().nullable(),
  link: z.string().nullable(),
  stageId: z.string().nullable(),
  /**
   * Body do timeline_event (notas longas, descrição de marcos etc).
   * Só populado quando `source === 'timeline_event'`.
   */
  description: z.string().nullable().optional(),
  /** UID do autor — só pra source='timeline_event'. */
  createdBy: z.string().optional(),
  /** Nome resolvido via JOIN users — só pra source='timeline_event'. */
  createdByName: z.string().optional(),
  /** ISO timestamp de criação da row — só pra source='timeline_event'. */
  createdAt: z.string().optional(),
  /** Foto count via subquery (status='ready') — só pra source='timeline_event'. */
  photoCount: z.number().int().min(0).optional(),
});
export type TimeviewEvent = z.infer<typeof timeviewEventSchema>;

/**
 * Stage row pro modo gantt — mesmo shape do `/v1/gantt` legado, mas
 * formalizado em schema pra que o client possa reaproveitar via
 * `@builder/shared` em vez de duplicar tipo.
 */
export const timeviewStageSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  sort_order: z.number().int(),
  status: z.string(),
  progress_percent: z.number(),
  start_date: z.string().nullable(),
  end_date: z.string().nullable(),
  planned_start_date: z.string().nullable(),
  planned_end_date: z.string().nullable(),
  plannedMs: z.number(),
  actualMs: z.number(),
  critical: z.boolean(),
});
export type TimeviewStage = z.infer<typeof timeviewStageSchema>;

export const timeviewDependencySchema = z.object({
  from: z.string(),
  to: z.string(),
});
export type TimeviewDependency = z.infer<typeof timeviewDependencySchema>;

export const timeviewResponseSchema = z.object({
  projectId: z.string(),
  from: isoDateSchema,
  to: isoDateSchema,
  events: z.array(timeviewEventSchema),
  stages: z.array(timeviewStageSchema),
  dependencies: z.array(timeviewDependencySchema),
  holidays: z.array(feriadoSchema),
  generatedAt: z.string(),
});
export type TimeviewResponse = z.infer<typeof timeviewResponseSchema>;
